import { FetchRequestState } from 'types'
import { InformalFeedbackRequest, InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { useAuthOrUnregisteredUser } from './useAuthOrUnregisteredUser'
import { useInformalFeedbackRequests as useInformalFeedbackRequestsQuery } from './queries/informalFeedbackRequests/useInformalFeedbackRequests'
import { QueryObserverResult } from 'react-query/types/core/types'

// TODO: Delete and replace once reviews as sharing is released
export interface ArticleInformalFeedbackRequests {
  informalFeedbackRequests: InformalFeedbackRequest[]
  requestedByMe: InformalFeedbackRequest[]
  requestedByOthers: InformalFeedbackRequest[]
  mostRecentClosedRequest?: InformalFeedbackRequest
  loadingState: FetchRequestState
}

const loadingStateFromQuery = ({ isError, isLoading, isSuccess }: QueryObserverResult) => {
  if (isLoading) {
    return FetchRequestState.Loading
  }

  if (isError) {
    return FetchRequestState.Rejected
  }

  if (isSuccess) {
    return FetchRequestState.Resolved
  }

  return FetchRequestState.Idle
}

export const useInformalFeedbackRequests = (
  articleKey: string,
  layerId: string,
  options: {
    staleTime?: number
    states?: InformalFeedbackRequestState[]
    enabled?: boolean
  } = { enabled: true },
): ArticleInformalFeedbackRequests & QueryObserverResult => {
  const userId = useAuthOrUnregisteredUser().user?.id
  const informalFeedbackRequestsQueryResult = useInformalFeedbackRequestsQuery({
    articleKey,
    layerId,
    states: [InformalFeedbackRequestState.Open, InformalFeedbackRequestState.Closed],
    ...options,
  })
  const queryResult = informalFeedbackRequestsQueryResult
  const { data } = queryResult
  const informalFeedbackRequests =
    data?.informal_feedback_requests.filter((request) => {
      return (
        request.state === InformalFeedbackRequestState.Open ||
        (request.state === InformalFeedbackRequestState.Closed &&
          request.requester.id === userId &&
          !request.cleanup_dismissed)
      )
    }) || []
  const loadingState = loadingStateFromQuery(informalFeedbackRequestsQueryResult)

  const requestedByMe = informalFeedbackRequests.filter(({ requester }) => requester.id === userId)
  const requestedByOthers = informalFeedbackRequests.filter(({ requester }) => requester.id !== userId)
  const mostRecentClosedRequest = data?.informal_feedback_requests.find((request) => {
    return request.state === InformalFeedbackRequestState.Closed
  })

  return {
    requestedByMe,
    requestedByOthers,
    informalFeedbackRequests,
    loadingState,
    mostRecentClosedRequest,
    ...queryResult,
  }
}
